import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { ProgressBar } from 'primereact/progressbar';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import configuration from './configuration.json';
import './dbFromCorpusPapers.css';

const CFG = (configuration && configuration.passer) || {};
// When the React app is loaded from a developer's machine (localhost),
// call the local ingestion API. When the bundle is served from any other
// host (production deployment, LAN, etc.), call the remote IngestAPI on
// 195.230.127.227 — 127.0.0.1 from a remote machine refers to *that*
// machine's loopback, not the developer's. localStorage('ingestAPI')
// still wins per the useState() below if a manual override is set.
const IS_LOCAL_HOST = typeof window !== 'undefined'
    && ['localhost', '127.0.0.1', '0.0.0.0'].includes(window.location.hostname);
const DEFAULT_INGEST_API = IS_LOCAL_HOST
    ? (CFG.IngestAPI       || 'http://127.0.0.1:8010')
    : (CFG.IngestAPIRemote || 'https://passer.scpdx.net/ingest');
const CHROMA_URLS = (CFG.Chroma || []).map((c) => c.url);
const OLLAMA_URLS = (CFG.Ollama || []).map((o) => o.url);
const DEFAULT_CHROMA = CHROMA_URLS[0] || 'http://127.0.0.1:8000';
const DEFAULT_OLLAMA = OLLAMA_URLS[0] || 'http://127.0.0.1:11434';

// PrimeReact's <ProgressBar> renders a CSS shimmer/gradient overlay that
// keeps animating even when value === 100. After ingestion finishes the
// bar would flash forever; for that case we render a flat coloured div
// instead. Same shape pattern as scorePapersBat.js' ProgressBarStatic.
function StaticBar({ pct, color = '#2196f3', height = 18 }) {
    const p = Math.max(0, Math.min(100, pct || 0));
    return (
        <div style={{ height, background: '#e0e0e0', borderRadius: 4,
                      overflow: 'hidden', position: 'relative' }}>
            <div style={{ width: `${p}%`, height: '100%',
                          background: color, borderRadius: 4,
                          transition: 'width 0.25s' }} />
        </div>
    );
}

const DBFromCorpusPapers = () => {
    const [ingestAPI, setIngestAPI] = useState(localStorage.getItem('ingestAPI') || DEFAULT_INGEST_API);
    const [corpusDir, setCorpusDir] = useState(localStorage.getItem('corpusDir') || 'newRAG/corpus');
    const [chromaUrl, setChromaUrl] = useState(localStorage.getItem('selectedChromaDB') || DEFAULT_CHROMA);
    const [ollamaUrl, setOllamaUrl] = useState(localStorage.getItem('selectedOllama') || DEFAULT_OLLAMA);
    const [collection, setCollection] = useState(localStorage.getItem('papersCollection') || 'papers_corpus');
    // Embedding model default. Order of preference:
    //   1. `papersEmbedModel` — explicit pick from THIS ingestion form (persists).
    //   2. `mxbai-embed-large` — the canonical project embedder. All
    //      experiment retrieval scripts assume mxbai (1024-dim). Falling
    //      back to `selectedLLMModel` here was wrong: the chat LLM
    //      (mistral / llama / etc.) is a 4096-dim hidden-state encoder
    //      that produces incompatible vectors, which 400s every later
    //      `/query` call against the resulting collection. See the
    //      manageDB dimension-mismatch warning for the diagnostic.
    //
    // Self-healing: a separate useEffect below runs on mount; if the
    // persisted value LOOKS like a chat model (mistral / llama / gemma /
    // qwen / phi / mixtral / codellama prefix), it gets force-reset to
    // mxbai. Real embedders (mxbai, nomic, bge, snowflake, all-minilm)
    // pass through untouched.
    const [embedModel, setEmbedModel] = useState(
        localStorage.getItem('papersEmbedModel')
        || 'mxbai-embed-large'
    );
    // Phase 5k: a generative LLM is needed (in addition to the embedder)
    // when the chosen profile generates per-chapter synopses. The model
    // name reuses the same conversational model the rest of the app
    // already uses (defaults to selectedLLMModel) so users don't have to
    // configure two unrelated dropdowns.
    const [llmModel, setLlmModel] = useState(
        localStorage.getItem('synopsisLLMModel')
        || localStorage.getItem('selectedLLMModel')
        || 'mistral'
    );
    // Phase 5h+: for first-person novels, the coref pass needs the
    // protagonist's name in order to substitute I/me/my with it outside
    // dialogue. Empty string is fine (third-person coref still runs)
    // but quality on questions about the protagonist himself suffers.
    const [protagonistName, setProtagonistName] = useState(
        localStorage.getItem('corefProtagonistName') || ''
    );
    // Coref-ablation toggles (companion to §V.B's synopsis_naive). Each
    // checkbox disables a specific part of the novel-profile coref pass
    // so the same corpus can be ingested under multiple configurations
    // and compared via retrieval metrics. All three default to off →
    // normal coref behaviour preserved.
    const [corefDisabled, setCorefDisabled] = useState(
        localStorage.getItem('corefDisabled') === 'true'
    );
    const [dialogueQualifierDisabled, setDialogueQualifierDisabled] = useState(
        localStorage.getItem('dialogueQualifierDisabled') === 'true'
    );
    const [attributionDisabled, setAttributionDisabled] = useState(
        localStorage.getItem('attributionDisabled') === 'true'
    );
    // §V.B synopsis-span ablation (BdKCSE-ext paper). When ON, each
    // chapter's synopsis LLM call receives ONLY the ~40-char chapter
    // heading instead of the full chapter→subsection span. Backend
    // stamps a -naive suffix onto chunk IDs so a naïve collection can
    // coexist with its hierarchy-aware sibling under different names.
    // Applies to any profile that produces synopses (academic / novel).
    const [synopsisNaive, setSynopsisNaive] = useState(
        localStorage.getItem('synopsisNaive') === 'true'
    );
    // baseline_vs_vanilla future-experiment toggle. Gated on the active
    // profile having `exclude_from_default_retrieval` non-empty (i.e.
    // some section the profile already considers retrieval-noise).
    // When ON, those sections are dropped at chunking time entirely
    // (not just filtered at retrieval) and chunk IDs gain a -noref
    // suffix so the "with refs" and "without refs" collections can
    // coexist in Chroma.
    const [excludeReferences, setExcludeReferences] = useState(
        localStorage.getItem('excludeReferences') === 'true'
    );
    const [chunkSize, setChunkSize] = useState(800);
    const [overlap, setOverlap] = useState(120);
    const [limit, setLimit] = useState('');
    // Corpus profile — see newRAG/corpus_profiles.py and
    // newRAG/RAG_GENERALIZATION.md. The available profiles are fetched
    // from the worker's /profiles endpoint on mount. Default is
    // "academic_paper", which reproduces v1 behaviour exactly.
    const [profile, setProfile] = useState(localStorage.getItem('papersProfile') || 'academic_paper');
    const [profiles, setProfiles] = useState([]);     // [{name, chunk_size, chunk_overlap, section_names, exclude_from_default_retrieval, ...}]

    const [serverOK, setServerOK] = useState(null);
    const [status, setStatus] = useState({ running: false, total: 0, done: 0, errors: 0, last_file: '', error: null, results: [], chunk_done: 0, chunk_total: 0, synopsis_done: 0, synopsis_total: 0, coref_done: 0, coref_total: 0 });
    const [papers, setPapers] = useState([]);
    const [busy, setBusy] = useState(false);
    const [collInfo, setCollInfo] = useState(null);
    const [browseOpen, setBrowseOpen] = useState(false);
    const [browseInfo, setBrowseInfo] = useState({ path: '', subdirs: [], pdf_count: 0, parent: null, cwd: '' });
    const [browseLoading, setBrowseLoading] = useState(false);
    const [finishSummary, setFinishSummary] = useState(null);
    // Live probe of the typed corpus directory: tells the user "✓ 17 PDFs
    // found" / "⚠ no PDFs here" / "⚠ path not found" without having to
    // open the browse dialog. Debounced so we don't hammer /list_dir on
    // every keystroke.
    const [corpusProbe, setCorpusProbe] = useState({ loading: false, pdfCount: null, error: null });
    const pollRef = useRef(null);
    const prevRunningRef = useRef(false);
    // Local-disk → server upload. The browser's file picker shows the
    // USER's filesystem (their Mac), files chosen there get POSTed to
    // the backend's /upload endpoint which creates a fresh server-side
    // folder and returns its absolute path; we write that into
    // corpusDir so the subsequent /start request points the worker at
    // the freshly-uploaded files.
    //
    // Two input refs: one accepts individual files, the other (with
    // webkitdirectory) accepts an entire folder so reviewers can grab
    // a whole Jane Eyre / academic-papers folder in one shot. Same
    // handler for both — only the input attributes differ.
    const fileInputRef   = useRef(null);
    const folderInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    // Shared-worker availability poll. Hits /status every 5 s
    // INDEPENDENT of the run-detection poll, so the "Server busy" banner
    // updates even when this user isn't doing anything. Lets reviewers
    // know if someone else's job is in progress before they hit Start
    // and get a 409.
    const [worker, setWorker] = useState({
        loaded: false,    // first poll has come back
        reachable: false,
        running: false,
        analyst: '',
        collection: '',
        profile: '',
        started_at: '',
        done: 0,
        total: 0,
        coref_done: 0,
        coref_total: 0,
    });
    useEffect(() => {
        let cancelled = false;
        const pollWorker = async () => {
            try {
                const r = await axios.get(
                    ingestAPI.replace(/\/$/, '') + '/status',
                    { timeout: 4000 });
                if (cancelled) return;
                const s = r.data || {};
                setWorker({
                    loaded: true, reachable: true,
                    running: !!s.running,
                    analyst:    s.analyst    || '',
                    collection: s.collection || '',
                    profile:    s.profile    || '',
                    started_at: s.started_at || '',
                    done:       s.done       || 0,
                    total:      s.total      || 0,
                    coref_done:  s.coref_done  || 0,
                    coref_total: s.coref_total || 0,
                });
            } catch (_) {
                if (!cancelled)
                    setWorker(w => ({ ...w, loaded: true,
                                       reachable: false, running: false }));
            }
        };
        pollWorker();
        const id = setInterval(pollWorker, 5000);
        return () => { cancelled = true; clearInterval(id); };
    }, [ingestAPI]);

    // ETA helper: time since started + projected remaining based on
    // chunk progress. Coref runs are dominated by coref_done / coref_total
    // so prefer that when those numbers are populated.
    const fmtElapsed = (ms) => {
        if (ms < 0) ms = 0;
        const s = Math.round(ms / 1000);
        const h = Math.floor(s / 3600);
        const m = Math.floor((s % 3600) / 60);
        if (h > 0) return `${h}h ${m}m`;
        return `${m}m`;
    };
    const handleUpload = async (ev) => {
        const files = Array.from(ev.target.files || []);
        if (files.length === 0) return;
        // When uploaded via webkitdirectory, the browser includes the
        // selected folder name as the first segment of file.webkitRelativePath
        // (e.g. "JaneEyre/jane_eyre.pdf"). We keep only files whose name
        // looks ingestable (PDF / TXT) so the user can pick a messy folder
        // and we'll quietly skip the .DS_Store etc.
        const accepted = files.filter(f =>
            /\.(pdf|txt)$/i.test(f.name)
        );
        if (accepted.length === 0) {
            alert(`No PDF / TXT files found in your selection (${files.length} file(s) checked).`);
            return;
        }
        setUploading(true);
        setUploadProgress(0);
        const fd = new FormData();
        accepted.forEach(f => fd.append('files', f));
        try {
            const r = await axios.post(
                ingestAPI.replace(/\/$/, '') + '/upload',
                fd,
                {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    onUploadProgress: (e) => {
                        if (e.total) {
                            setUploadProgress(Math.round(e.loaded / e.total * 100));
                        }
                    },
                    timeout: 30 * 60 * 1000,    // 30 min for huge corpora
                }
            );
            const { corpus_dir, count, total_size } = r.data || {};
            if (corpus_dir) {
                setCorpusDir(corpus_dir);
                localStorage.setItem('corpusDir', corpus_dir);
                setCorpusProbe({
                    loading: false,
                    pdfCount: count || 0,
                    error: null,
                });
            }
            const mb = (total_size / (1024 * 1024)).toFixed(1);
            const skipped = files.length - accepted.length;
            alert(
                `Uploaded ${count} file(s) (${mb} MB) into\n${corpus_dir}\n\n` +
                (skipped > 0 ? `(skipped ${skipped} non-PDF/TXT entr${skipped === 1 ? 'y' : 'ies'})\n\n` : '') +
                `The "Corpus directory" field has been updated; you can now click Start.`
            );
        } catch (e) {
            alert('Upload failed: ' +
                  (e.response?.data?.error || e.message));
        } finally {
            setUploading(false);
            setUploadProgress(0);
            if (fileInputRef.current)   fileInputRef.current.value = '';
            if (folderInputRef.current) folderInputRef.current.value = '';
        }
    };

    const speak = (text) => {
        try {
            const u = new SpeechSynthesisUtterance(text);
            window.speechSynthesis.speak(u);
        } catch (_) { /* no-op */ }
    };

    const formatDuration = (sec) => {
        if (!sec || sec < 0) return '—';
        const m = Math.floor(sec / 60), s = Math.round(sec % 60);
        return m > 0 ? `${m}m ${s}s` : `${s}s`;
    };

    const checkServer = useCallback(async () => {
        try {
            const r = await axios.get(ingestAPI.replace(/\/$/, '') + '/health', { timeout: 3000 });
            setServerOK(!!r.data.ok);
        } catch (e) {
            setServerOK(false);
        }
    }, [ingestAPI]);

    // Fetch the list of registered corpus profiles from the worker.
    // Each entry carries chunk_size / chunk_overlap defaults; selecting
    // a profile auto-fills those inputs (the user can still override).
    const fetchProfiles = useCallback(async () => {
        try {
            const r = await axios.get(ingestAPI.replace(/\/$/, '') + '/profiles', { timeout: 5000 });
            const list = r.data?.profiles || [];
            setProfiles(list);
            // If the persisted profile isn't in the list, fall back to default
            if (!list.find(p => p.name === profile)) {
                setProfile(r.data?.default || 'academic_paper');
            }
        } catch (_) {
            // Old workers without /profiles — silently default
            setProfiles([{ name: 'academic_paper',
                           chunk_size: 800, chunk_overlap: 120,
                           section_names: [], exclude_from_default_retrieval: [] }]);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ingestAPI]);

    // When the selected profile changes, refresh the chunk/overlap
    // defaults from the profile (user can still override afterwards).
    const onProfileChange = (newName) => {
        setProfile(newName);
        localStorage.setItem('papersProfile', newName);
        const p = profiles.find(x => x.name === newName);
        if (p) {
            if (p.chunk_size)    setChunkSize(String(p.chunk_size));
            if (p.chunk_overlap) setOverlap(String(p.chunk_overlap));
        }
    };

    // Helper for the dropdown options (passed to PrimeReact Dropdown).
    const profileOptions = useMemo(() => profiles.map(p => ({
        label: p.name === 'academic_paper'
                ? `academic_paper — scientific papers (default; IMRaD sections, references excluded)`
                : p.name === 'novel'
                ? `novel — long-form fiction (chapters / parts / appendices)`
                : p.name === 'manual'
                ? `manual — technical / workbook (hierarchical sections, procedures)`
                : p.name,
        value: p.name,
    })), [profiles]);

    const currentProfile = useMemo(() =>
        profiles.find(p => p.name === profile) || null,
    [profiles, profile]);

    const pollStatus = useCallback(async () => {
        try {
            const r = await axios.get(ingestAPI.replace(/\/$/, '') + '/status', { timeout: 5000 });
            const s = r.data || {};
            setStatus(s);
            // detect a true → false transition (run just finished)
            if (prevRunningRef.current && s.running === false) {
                const duration = (s.finished_ts && s.start_ts) ? (s.finished_ts - s.start_ts) : null;
                setFinishSummary({
                    done: s.done, total: s.total, errors: s.errors,
                    duration, error: s.error,
                    finishedAt: new Date().toLocaleTimeString(),
                    chain_logimport: s.chain_logimport,
                    chain_logimport_tx: s.chain_logimport_tx,
                    chain_logimport_msg: s.chain_logimport_msg,
                });
                speak(s.error
                    ? 'Ingestion finished with errors'
                    : `Ingestion finished. ${s.done} of ${s.total} papers.`);
                // Phase 5n: the chain logimport is now fired by the
                // worker itself at end of run_ingestion (see
                // newRAG/ingest_corpus.py). The previous client-side
                // POST here would have left the audit log empty whenever
                // the browser tab was closed during a long run; moving
                // it to the worker removes that dependency. The result
                // is surfaced in s.chain_logimport ("ok" / "error" /
                // "skipped") for the UI to display.
            }
            prevRunningRef.current = !!s.running;
            if (s.running === false && pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
                setBusy(false);
            }
        } catch (e) {
            // Transient network blip — skip THIS tick, keep the interval
            // alive. Previously we killed the interval on the first
            // error, which silently froze the Progress section at the
            // last good snapshot for the rest of the run. The
            // every-5 s worker poll (used by the top "Server busy"
            // banner) is the authoritative liveness signal; pollStatus
            // here is purely for the in-page progress display. Better
            // to retry on the next 1.5 s tick than to give up.
            console.warn('[dbFromCorpusPapers] /status poll failed, retrying:',
                         e?.message || e);
        }
    }, [ingestAPI]);

    const browseAt = useCallback(async (path) => {
        setBrowseLoading(true);
        try {
            const r = await axios.get(ingestAPI.replace(/\/$/, '') + '/list_dir', {
                params: path ? { path } : {},
                timeout: 10000,
            });
            setBrowseInfo(r.data || { path, subdirs: [], pdf_count: 0, parent: null });
        } catch (e) {
            alert('Browse failed: ' + (e.response?.data?.error || e.message));
        } finally {
            setBrowseLoading(false);
        }
    }, [ingestAPI]);

    const openBrowser = () => {
        setBrowseOpen(true);
        browseAt(corpusDir || '');
    };

    const probeCollection = useCallback(async () => {
        try {
            const r = await axios.get(ingestAPI.replace(/\/$/, '') + '/collection_info', {
                params: { chroma: chromaUrl, collection },
                timeout: 5000,
            });
            setCollInfo(r.data || null);
        } catch (e) {
            setCollInfo(null);
        }
    }, [ingestAPI, chromaUrl, collection]);

    // Unconditional default on every page entry (mount or hard refresh).
    // The Embed Model field ALWAYS shows mxbai-embed-large when this
    // page loads — the canonical project embedder, 1024-dim, what every
    // experiment retrieval script assumes. If the user wants a different
    // embedder for THIS run, they type it into the field AFTER the page
    // has loaded; the onChange handler persists their choice to
    // localStorage, but the next page entry overrides it again so the
    // canonical default is the rule, not the persisted exception.
    useEffect(() => {
        const CANONICAL = 'mxbai-embed-large';
        if (embedModel !== CANONICAL) {
            console.log(`[ingestion] resetting embed model to ${CANONICAL} `
                       + `(was: "${embedModel}"). Override after load if needed.`);
            setEmbedModel(CANONICAL);
            localStorage.setItem('papersEmbedModel', CANONICAL);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        checkServer();
        fetchProfiles();
        // Initial /status probe: ONLY adopt the worker's state if a job is
        // actively running (e.g. another tab kicked one off, or the user
        // reloaded mid-ingest). For an idle/completed worker we leave the
        // initial empty status untouched so a reload doesn't show the
        // previous run's "done X/Y" panel.
        (async () => {
            try {
                const r = await axios.get(ingestAPI.replace(/\/$/, '') + '/status', { timeout: 5000 });
                const s = r.data || {};
                if (s.running) {
                    setStatus(s);
                    prevRunningRef.current = true;
                    setBusy(true);
                    if (!pollRef.current) {
                        pollRef.current = setInterval(pollStatus, 1500);
                    }
                }
            } catch (_) { /* checkServer surfaces unreachable state */ }
        })();
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, [checkServer, pollStatus, fetchProfiles, ingestAPI]);

    useEffect(() => { probeCollection(); }, [probeCollection]);

    // Watchdog: keep the in-page /status poll alive whenever the worker
    // says a run is in progress. Covers three failure modes that used
    // to silently freeze the Progress section:
    //   1. User refreshed/navigated to this page AFTER another tab (or
    //      the same tab pre-refresh) started the ingestion — the mount
    //      probe ran but raced the run start.
    //   2. A transient network error in pollStatus cleared the
    //      interval before the catch block was hardened.
    //   3. The local interval was started fine but the effect that
    //      manages it tore it down on a re-render.
    // The worker poll (every 5 s) IS authoritative for `worker.running`;
    // we mirror that into the local fast poll here. When worker.running
    // flips to false, we let pollStatus itself clean up — the existing
    // `s.running === false` branch already handles it.
    useEffect(() => {
        if (worker.running && !pollRef.current) {
            pollStatus();   // immediate tick so we don't wait 1.5 s
            pollRef.current = setInterval(pollStatus, 1500);
        }
    }, [worker.running, pollStatus]);

    // Live PDF-count probe for the corpus directory input. Debounced
    // 400 ms so we don't hammer /list_dir while the user is typing.
    // Empty path or unreachable server -> clear the probe display.
    useEffect(() => {
        if (!corpusDir || !serverOK) {
            setCorpusProbe({ loading: false, pdfCount: null, error: null });
            return undefined;
        }
        const t = setTimeout(async () => {
            setCorpusProbe(p => ({ ...p, loading: true }));
            try {
                const r = await axios.get(ingestAPI.replace(/\/$/, '') + '/list_dir', {
                    params: { path: corpusDir },
                    timeout: 5000,
                });
                setCorpusProbe({
                    loading: false,
                    pdfCount: r.data?.pdf_count ?? 0,
                    error: null,
                });
            } catch (e) {
                setCorpusProbe({
                    loading: false,
                    pdfCount: null,
                    error: e.response?.data?.error || 'path not found',
                });
            }
        }, 400);
        return () => clearTimeout(t);
    }, [corpusDir, serverOK, ingestAPI]);

    const startIngest = async () => {
        if (collInfo && collInfo.exists && collInfo.embed_model
            && collInfo.embed_model !== embedModel) {
            const ok = window.confirm(
                `Collection "${collection}" was built with embed_model="${collInfo.embed_model}", ` +
                `but you are about to ingest with "${embedModel}". ` +
                `The server will refuse this — change the embedding model to "${collInfo.embed_model}" ` +
                `or use a different collection name. Continue anyway (it will fail)?`
            );
            if (!ok) return;
        }
        setFinishSummary(null);
        prevRunningRef.current = true; // arm finish-detection for this run
        setBusy(true);
        try {
            const body = {
                corpus_dir: corpusDir,
                chroma_url: chromaUrl,
                ollama_url: ollamaUrl,
                collection,
                embed_model: embedModel,
                profile,                                        // ← new
                chunk_size: Number(chunkSize) || 0,            // 0 → worker uses profile default
                overlap:    Number(overlap)    || 0,
            };
            // The LLM is needed for synopsis generation AND/OR
            // coreference resolution. Either one triggers sending the
            // model. Profiles without LLM passes (academic_paper) get
            // a clean payload without these fields.
            if (currentProfile?.synopsize || currentProfile?.coref) {
                body.llm_model = llmModel;
                body.llm_url   = ollamaUrl;
            }
            // First-person attribution. Only meaningful when coref runs;
            // otherwise the worker ignores it.
            if (currentProfile?.coref && protagonistName.trim()) {
                body.protagonist_name = protagonistName.trim();
            }
            // Coref-ablation toggles. Only sent when set so the request
            // body stays clean for standard ingestions, and so older
            // workers without these opts simply ignore the unknown
            // fields rather than 4xx-ing.
            if (currentProfile?.coref && corefDisabled) {
                body.coref_disabled = true;
            }
            if (currentProfile?.coref && dialogueQualifierDisabled) {
                body.dialogue_qualifier_disabled = true;
            }
            if (currentProfile?.coref && attributionDisabled) {
                body.attribution_disabled = true;
            }
            if (currentProfile?.synopsize && synopsisNaive) {
                body.synopsis_naive = true;
            }
            // Only sent when the profile has something to exclude (e.g.
            // academic_paper's "references" set); for profiles with an
            // empty exclude_from_default_retrieval the toggle is hidden
            // and this is a no-op.
            if ((currentProfile?.exclude_from_default_retrieval || []).length
                && excludeReferences) {
                body.exclude_references = true;
            }
            // Phase 5n: analyst name for the server-side chain
            // logimport call (worker fires it at end of run_ingestion,
            // not the React client anymore). Same localStorage key the
            // old client-side call used so existing users don't lose
            // their attribution.
            body.analyst = localStorage.getItem('wharf_user_name') || 'anonymous';
            const lim = parseInt(limit, 10);
            if (!isNaN(lim) && lim > 0) body.limit = lim;
            localStorage.setItem('papersCollection', collection);
            localStorage.setItem('papersEmbedModel', embedModel);
            localStorage.setItem('synopsisLLMModel', llmModel);
            localStorage.setItem('papersProfile',    profile);
            localStorage.setItem('corpusDir', corpusDir);
            localStorage.setItem('ingestAPI', ingestAPI);
            await axios.post(ingestAPI.replace(/\/$/, '') + '/start', body, { timeout: 10000 });
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = setInterval(pollStatus, 1500);
        } catch (e) {
            alert('Could not start ingestion: ' + (e.response?.data?.error || e.message));
            setBusy(false);
        }
    };

    const stopIngest = async () => {
        // Pass our own analyst identity so the backend can verify we're
        // the owner of the running run. The backend rejects with 403
        // when caller ≠ STATUS["analyst"], so reviewer B can never
        // break reviewer A's job even by curl-ing /stop directly.
        const me = localStorage.getItem('wharf_user_name') || 'anonymous';
        try {
            await axios.post(
                ingestAPI.replace(/\/$/, '') + '/stop',
                { analyst: me },
                { timeout: 5000 });
        } catch (e) {
            const code = e?.response?.status;
            if (code === 403) {
                alert(`Cannot stop — this ingestion was started by `
                    + `${e.response.data.owner}, not you.`);
            }
        }
    };

    const dropCollection = async () => {
        if (!collection) return;
        const ok = window.confirm(
            `Permanently delete collection "${collection}" from ChromaDB at ${chromaUrl}?\n` +
            `This is the recommended recovery if the worker reports KeyError('_type').`
        );
        if (!ok) return;
        try {
            const r = await axios.post(ingestAPI.replace(/\/$/, '') + '/delete_collection',
                { chroma_url: chromaUrl, collection }, { timeout: 15000 });
            alert(r.data?.deleted ? `Dropped "${collection}".` : `Did not delete: ${JSON.stringify(r.data)}`);
            await probeCollection();
        } catch (e) {
            alert('Drop failed: ' + (e.response?.data?.error || e.message));
        }
    };

    const listPapers = async () => {
        try {
            const r = await axios.get(ingestAPI.replace(/\/$/, '') + '/papers', {
                params: { chroma: chromaUrl, collection },
                timeout: 30000,
            });
            setPapers(r.data?.papers || []);
        } catch (e) {
            alert('Could not list papers: ' + (e.response?.data?.error || e.message));
        }
    };

    const pct = status.total ? Math.floor((status.done / status.total) * 100) : 0;
    const recent = (status.results || []).slice(-25).reverse();

    // Top-of-page banner: at-a-glance "Available / Busy / Unreachable"
    // status for the SHARED ingestion worker. Updates every 5 s via the
    // pollWorker effect above. Hides itself until the first poll comes
    // back (avoids a flash of misinformation).
    const renderAvailabilityBanner = () => {
        if (!worker.loaded) return null;
        if (!worker.reachable) {
            return (
                <div style={{
                    background: '#fee2e2', color: '#991b1b',
                    padding: '10px 14px', borderRadius: 8,
                    marginBottom: 12, fontSize: 13, fontWeight: 600,
                    display: 'flex', alignItems: 'center', gap: 8,
                }}>
                    <i className="pi pi-times-circle" />
                    Ingestion worker unreachable at <code>{ingestAPI}</code>.
                    <span style={{ fontWeight: 400, color: '#7f1d1d' }}>
                        Either the local backend isn't running, or the
                        remote service is down.
                    </span>
                </div>
            );
        }
        if (worker.running) {
            // Top banner is kept on every page (including this one) —
            // its elapsed-time + ETA + analyst+collection summary is
            // useful even when the user owns the running job. The
            // detailed Progress section below adds per-counter live
            // numbers; the two are complementary. They poll at
            // different intervals (5 s vs 1.5 s) so the numbers may
            // momentarily disagree by one tick — that's expected and
            // self-corrects on the next 5 s poll.

            // Elapsed time + progress / ETA for the active job
            let elapsedMs = 0;
            try {
                if (worker.started_at) {
                    elapsedMs = Date.now() - new Date(worker.started_at).getTime();
                }
            } catch (_) { /* fall through */ }
            // Prefer coref progress when meaningful, else fall back to
            // chunk progress, else file progress.
            let pct = 0, label = '';
            if (worker.coref_total > 0) {
                pct = Math.round(worker.coref_done / worker.coref_total * 100);
                label = `coref ${worker.coref_done}/${worker.coref_total}`;
            } else if (worker.total > 0) {
                pct = Math.round(worker.done / worker.total * 100);
                label = `${worker.done}/${worker.total} files`;
            }
            let etaStr = '';
            if (pct > 0 && pct < 100 && elapsedMs > 5000) {
                const remainingMs = (elapsedMs / pct) * (100 - pct);
                etaStr = ` · ETA ~${fmtElapsed(remainingMs)}`;
            }
            return (
                <div style={{
                    background: '#fef3c7', color: '#92400e',
                    padding: '10px 14px', borderRadius: 8,
                    marginBottom: 12, fontSize: 13,
                    display: 'flex', alignItems: 'center', gap: 12,
                    flexWrap: 'wrap',
                }}>
                    <i className="pi pi-spin pi-spinner" />
                    <strong>Server busy</strong>
                    <span>—&nbsp;
                        <code>{worker.analyst || 'anonymous'}</code> is
                        ingesting into <code>{worker.collection || '?'}</code>
                        {worker.profile && <> (profile <code>{worker.profile}</code>)</>}.
                    </span>
                    <span style={{ marginLeft: 'auto', fontFamily: 'ui-monospace, Menlo, monospace' }}>
                        {label && <>{label} ({pct}%) · </>}
                        elapsed {fmtElapsed(elapsedMs)}{etaStr}
                    </span>
                </div>
            );
        }
        return (
            <div style={{
                background: '#d1fae5', color: '#065f46',
                padding: '10px 14px', borderRadius: 8,
                marginBottom: 12, fontSize: 13, fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: 8,
            }}>
                <i className="pi pi-check-circle" />
                Ingestion worker available — no run in progress.
            </div>
        );
    };

    return (
        <div className="dbFromCorpusPapers">
            <h2>📚 Build Vectorstore from Paper Corpus</h2>

            {renderAvailabilityBanner()}

            <div className="grid-2col">
                <div className="card">
                    <h3>🛠 Ingestion Worker</h3>
                    <label>Ingestion API URL</label>
                    <div className="row">
                        <InputText value={ingestAPI} onChange={(e) => setIngestAPI(e.target.value)} style={{ flex: 1 }} />
                        <Button label="Check" size="small" onClick={checkServer} />
                    </div>
                    <div style={{ marginTop: 6 }}>
                        Server: {serverOK === null ? <Tag value="checking…" /> :
                            serverOK ? <Tag severity="success" value="reachable" /> :
                            <Tag severity="danger" value="unreachable" />}
                    </div>
                    <pre className="help">
{`Start it with:
  pip install pdfplumber pypdf chromadb flask flask-cors requests
  python newRAG/ingest_corpus.py
(default port 8010)`}
                    </pre>
                </div>

                <div className="card">
                    <h3>🗄 Targets</h3>
                    <label>ChromaDB URL</label>
                    {CHROMA_URLS.length > 1 ? (
                        <Dropdown
                            value={chromaUrl}
                            options={CHROMA_URLS}
                            editable
                            onChange={(e) => { setChromaUrl(e.value); localStorage.setItem('selectedChromaDB', e.value); }}
                        />
                    ) : (
                        <InputText value={chromaUrl} onChange={(e) => { setChromaUrl(e.target.value); localStorage.setItem('selectedChromaDB', e.target.value); }} />
                    )}
                    <label>Ollama URL</label>
                    {OLLAMA_URLS.length > 1 ? (
                        <Dropdown
                            value={ollamaUrl}
                            options={OLLAMA_URLS}
                            editable
                            onChange={(e) => { setOllamaUrl(e.value); localStorage.setItem('selectedOllama', e.value); }}
                        />
                    ) : (
                        <InputText value={ollamaUrl} onChange={(e) => { setOllamaUrl(e.target.value); localStorage.setItem('selectedOllama', e.target.value); }} />
                    )}
                    <label>Embedding Model (defaults to the app's selected LLM)</label>
                    <InputText
                        value={embedModel}
                        onChange={(e) => { setEmbedModel(e.target.value); localStorage.setItem('papersEmbedModel', e.target.value); }}
                    />
                    <label>Corpus profile (what kind of documents?)</label>
                    <Dropdown
                        value={profile}
                        options={profileOptions}
                        onChange={(e) => onProfileChange(e.value)}
                        placeholder="Loading profiles…"
                        style={{ width: '100%' }}
                    />
                    {currentProfile && (
                        <div className="hint" style={{ marginTop: 4, fontSize: 11 }}>
                            <strong>Detects:</strong>{' '}
                            <code style={{ fontSize: 10 }}>
                                {(currentProfile.section_names || []).join(', ') || '(no section patterns)'}
                            </code>
                            {currentProfile.exclude_from_default_retrieval &&
                             currentProfile.exclude_from_default_retrieval.length > 0 && (
                                <>
                                    {' · '}<strong>Excluded from default retrieval:</strong>{' '}
                                    <code style={{ fontSize: 10 }}>
                                        {currentProfile.exclude_from_default_retrieval.join(', ')}
                                    </code>
                                </>
                            )}
                            {' · '}<strong>Default chunk:</strong>{' '}
                            <code style={{ fontSize: 10 }}>
                                {currentProfile.chunk_size}/{currentProfile.chunk_overlap}
                            </code>
                            {currentProfile.synopsize && (
                                <>
                                    {' · '}<strong>Synopses:</strong>{' '}
                                    <code style={{ fontSize: 10 }}>
                                        {(currentProfile.synopsize_sections || []).join(', ') || '(all sections)'}
                                    </code>
                                </>
                            )}
                            {currentProfile.coref && (
                                <>
                                    {' · '}<strong style={{ color: '#b65c00' }}>Coref: on</strong>
                                    <span style={{ color: '#888' }}>
                                        {' '}(per-chunk pronoun rewrite — slow, large LLM cost)
                                    </span>
                                </>
                            )}
                        </div>
                    )}
                    {(currentProfile?.synopsize || currentProfile?.coref) && (
                        <>
                            <label style={{ marginTop: 6 }}>
                                LLM model
                                <span style={{ color: '#888', fontWeight: 'normal', marginLeft: 6 }}>
                                    (used for{' '}
                                    {[currentProfile?.synopsize && 'synopses',
                                      currentProfile?.coref && 'coref'].filter(Boolean).join(' + ')}
                                    ; idempotent on re-ingest)
                                </span>
                            </label>
                            <InputText
                                value={llmModel}
                                onChange={(e) => { setLlmModel(e.target.value); localStorage.setItem('synopsisLLMModel', e.target.value); }}
                                placeholder="e.g. mistral:latest"
                            />
                        </>
                    )}
                    {currentProfile?.synopsize && (
                        <>
                            <label style={{ marginTop: 10, fontWeight: 600 }}>
                                Synopsis-span ablation (§V.B BdKCSE-ext paper)
                            </label>
                            <label style={{ marginTop: 4, fontWeight: 'normal' }}>
                                <input
                                    type="checkbox"
                                    checked={synopsisNaive}
                                    onChange={(e) => { setSynopsisNaive(e.target.checked); localStorage.setItem('synopsisNaive', e.target.checked ? 'true' : 'false'); }}
                                />
                                &nbsp;Naïve synopsis spans (no hierarchy)
                                <span style={{ color: '#888', marginLeft: 6, fontSize: 11 }}>
                                    (LLM gets only the chapter heading instead of the full chapter→subsection scope; produces ~40-char summaries vs ~10 000 — control arm of the hierarchy-aware vs naïve comparison; chunk IDs get a <code>-naive</code> suffix)
                                </span>
                            </label>
                        </>
                    )}
                    {(currentProfile?.exclude_from_default_retrieval || []).length > 0 && (
                        <>
                            <label style={{ marginTop: 10, fontWeight: 600 }}>
                                Reference handling (baseline_vs_vanilla future experiments)
                            </label>
                            <label style={{ marginTop: 4, fontWeight: 'normal' }}>
                                <input
                                    type="checkbox"
                                    checked={excludeReferences}
                                    onChange={(e) => { setExcludeReferences(e.target.checked); localStorage.setItem('excludeReferences', e.target.checked ? 'true' : 'false'); }}
                                />
                                &nbsp;Exclude {(currentProfile?.exclude_from_default_retrieval || []).join(' / ')} during ingestion
                                <span style={{ color: '#888', marginLeft: 6, fontSize: 11 }}>
                                    (default: OFF → {(currentProfile?.exclude_from_default_retrieval || []).join('/')} are ingested AND tagged for retrieval-time filtering; ON → dropped entirely at chunking, no chunks created. Lets a no-references collection coexist with the standard one; chunk IDs get a <code>-noref</code> suffix.)
                                </span>
                            </label>
                        </>
                    )}
                    {currentProfile?.coref && (
                        <>
                            <label style={{ marginTop: 6 }}>
                                Protagonist name (for first-person novels)
                                <span style={{ color: '#888', fontWeight: 'normal', marginLeft: 6 }}>
                                    (substitutes I/me/my with this name outside dialogue; leave blank for third-person novels)
                                </span>
                            </label>
                            <InputText
                                value={protagonistName}
                                onChange={(e) => { setProtagonistName(e.target.value); localStorage.setItem('corefProtagonistName', e.target.value); }}
                                placeholder="e.g. John"
                            />
                            {/* Coref ablation toggles. Default off → normal
                                behaviour. Each one stamps a distinct suffix
                                onto the chunk IDs so the ablation collection
                                can coexist with the standard one. */}
                            <label style={{ marginTop: 10, fontWeight: 600 }}>
                                Ablation toggles (leave off for normal ingestion)
                            </label>
                            <label style={{ marginTop: 4, fontWeight: 'normal' }}>
                                <input
                                    type="checkbox"
                                    checked={corefDisabled}
                                    onChange={(e) => { setCorefDisabled(e.target.checked); localStorage.setItem('corefDisabled', e.target.checked ? 'true' : 'false'); }}
                                />
                                &nbsp;Disable coref entirely
                                <span style={{ color: '#888', marginLeft: 6, fontSize: 11 }}>
                                    (embeds original chunk text; baseline for the pronominal vs control comparison; chunk IDs get a <code>-nocoref</code> suffix)
                                </span>
                            </label>
                            <label style={{ marginTop: 2, fontWeight: 'normal' }}>
                                <input
                                    type="checkbox"
                                    checked={attributionDisabled}
                                    onChange={(e) => { setAttributionDisabled(e.target.checked); localStorage.setItem('attributionDisabled', e.target.checked ? 'true' : 'false'); }}
                                    disabled={corefDisabled}
                                />
                                &nbsp;Disable first-person attribution
                                <span style={{ color: '#888', marginLeft: 6, fontSize: 11 }}>
                                    (third-person coref still runs; I/me/my are NOT replaced with the protagonist name; chunk IDs get a <code>-noattr</code> suffix)
                                </span>
                            </label>
                            <label style={{ marginTop: 2, fontWeight: 'normal' }}>
                                <input
                                    type="checkbox"
                                    checked={dialogueQualifierDisabled}
                                    onChange={(e) => { setDialogueQualifierDisabled(e.target.checked); localStorage.setItem('dialogueQualifierDisabled', e.target.checked ? 'true' : 'false'); }}
                                    disabled={corefDisabled || attributionDisabled}
                                />
                                &nbsp;Disable dialogue qualifier
                                <span style={{ color: '#888', marginLeft: 6, fontSize: 11 }}>
                                    (rewrite first-person pronouns EVEN inside quoted dialogue; ablation only — introduces deliberate misattribution; chunk IDs get a <code>-nodq</code> suffix)
                                </span>
                            </label>
                        </>
                    )}
                    <label>Collection Name</label>
                    <InputText value={collection} onChange={(e) => setCollection(e.target.value)} />
                </div>
            </div>

            <div className="card">
                <h3>📂 Corpus & Chunking</h3>
                <div className="grid-4col">
                    <div>
                        <label>Corpus directory (server-side path)</label>
                        <div className="row">
                            <InputText
                                value={corpusDir}
                                onChange={(e) => setCorpusDir(e.target.value)}
                                placeholder="e.g. newRAG/corpus or /absolute/path/to/pdfs"
                                style={{ flex: 1 }}
                            />
                            <Button
                                label="Browse…"
                                icon="pi pi-folder-open"
                                size="small"
                                onClick={openBrowser}
                                disabled={!serverOK}
                            />
                            <Button
                                label={uploading
                                    ? `Uploading ${uploadProgress}%`
                                    : "Upload from browser"}
                                icon="pi pi-cloud-upload"
                                size="small"
                                severity="success"
                                loading={uploading}
                                disabled={!serverOK || uploading}
                                onClick={() => fileInputRef.current?.click()}
                                tooltip="Upload one or more PDFs from your own computer; the server creates a fresh corpus folder and sets the path field for you"
                                tooltipOptions={{ position: 'top' }}
                            />
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                accept=".pdf,.txt,application/pdf,text/plain"
                                style={{ display: 'none' }}
                                onChange={handleUpload}
                            />
                        </div>
                        {/* Live PDF-count feedback so the user knows the path
                            is valid before clicking Start (no need to open
                            the browse dialog). */}
                        <div style={{ marginTop: 4, fontSize: 12, minHeight: 16 }}>
                            {!serverOK
                                ? <span style={{ color: '#888' }}>worker offline — can't probe path</span>
                                : !corpusDir
                                    ? <span style={{ color: '#888' }}>enter or browse to a directory</span>
                                    : corpusProbe.loading
                                        ? <span style={{ color: '#888' }}>checking…</span>
                                        : corpusProbe.error
                                            ? <span style={{ color: '#d73a49' }}>⚠ {corpusProbe.error}</span>
                                            : corpusProbe.pdfCount === 0
                                                ? <span style={{ color: '#d4a017' }}>⚠ no PDFs in this directory</span>
                                                : corpusProbe.pdfCount !== null
                                                    ? <span style={{ color: '#22863a' }}>✓ {corpusProbe.pdfCount} PDF{corpusProbe.pdfCount === 1 ? '' : 's'} found</span>
                                                    : null}
                        </div>
                    </div>
                    <div>
                        <label>Chunk size</label>
                        <InputText value={chunkSize} onChange={(e) => setChunkSize(e.target.value)} />
                    </div>
                    <div>
                        <label>Overlap</label>
                        <InputText value={overlap} onChange={(e) => setOverlap(e.target.value)} />
                    </div>
                    <div>
                        <label>Limit (optional, for testing)</label>
                        <InputText value={limit} onChange={(e) => setLimit(e.target.value)} placeholder="e.g. 10" />
                    </div>
                </div>

                <div className="row" style={{ marginTop: 12 }}>
                    <Button
                        label={
                            busy || status.running
                                ? 'Running…'
                                : (worker.running
                                    ? `Server busy (${worker.analyst || 'someone'})`
                                    : 'Start Ingestion')
                        }
                        icon="pi pi-play"
                        onClick={startIngest}
                        // Also disable when ANOTHER reviewer's job is in
                        // progress — hitting /start would just 409. The
                        // banner above explains who's running.
                        disabled={busy || status.running || !serverOK || worker.running}
                        tooltip={worker.running
                            ? `Another ingestion is running (${worker.analyst || 'anonymous'} → ${worker.collection || '?'}). Wait until it finishes.`
                            : undefined}
                        tooltipOptions={{ position: 'top' }}
                    />
                    {/* Stop button: visible only to the analyst who started
                        the running job. When someone ELSE's job is running,
                        we hide the button entirely (rather than show a
                        disabled one) so reviewer B never thinks they can
                        manage reviewer A's run. The current user is read
                        from the same localStorage key as Anchor login. */}
                    {(() => {
                        // Use the SAME fallback as /start and the stop POST
                        // ("anonymous"). The old code used '' here, so a
                        // not-logged-in user who started the run as
                        // "anonymous" couldn't stop their own job.
                        const me = ((typeof window !== 'undefined' &&
                                     localStorage.getItem('wharf_user_name'))
                                    || 'anonymous').trim();
                        const anyRunning = status.running || worker.running;
                        const owner = (worker.analyst || '').trim();
                        const iAmOwner = !!owner && (me.toLowerCase() === owner.toLowerCase());
                        // Show Stop when: it's MY run, OR the worker hasn't
                        // reported an owner yet (poll race), OR the owner
                        // matches our anonymous fallback. We always render
                        // the button so the user has a recovery path; the
                        // backend is the final ownership gatekeeper (403).
                        const showStop = anyRunning && (!owner || iAmOwner);
                        if (!showStop) return null;
                        return (
                            <Button
                                label="Stop"
                                icon="pi pi-stop"
                                severity="danger"
                                onClick={stopIngest}
                                disabled={!anyRunning}
                                tooltip={`Stop your own ingestion (${owner || me} → ${worker.collection || collection || '?'})`}
                                tooltipOptions={{ position: 'top' }}
                            />
                        );
                    })()}
                    <Button
                        label="List indexed papers"
                        icon="pi pi-list"
                        severity="secondary"
                        onClick={listPapers}
                    />
                    <Button
                        label="Drop collection"
                        icon="pi pi-trash"
                        severity="warning"
                        outlined
                        onClick={dropCollection}
                        disabled={!collInfo || !collInfo.exists}
                        tooltip="Recovery for chromadb KeyError('_type')"
                    />
                </div>
            </div>

            {collInfo && collInfo.exists && (() => {
                const collProfile = collInfo.metadata?.profile;
                const embedMismatch   = collInfo.embed_model && collInfo.embed_model !== embedModel;
                const profileMismatch = collProfile          && collProfile          !== profile;
                const anyMismatch = embedMismatch || profileMismatch;
                return (
                    <div className={`card embedGuard ${anyMismatch ? 'mismatch' : 'match'}`}>
                        <strong>Collection check:</strong> "{collection}" exists with{' '}
                        <code>embed_model = {collInfo.embed_model || '(untagged)'}</code>,{' '}
                        <code>profile = {collProfile || '(untagged)'}</code>,{' '}
                        <code>{collInfo.count} chunks</code>.
                        {embedMismatch && (
                            <div className="err" style={{ marginTop: 8 }}>
                                ⚠ Embedding Model is <code>{embedModel}</code>. Ingestion will be
                                refused — vectors would live in a different space.
                                Either set Embedding Model to <code>{collInfo.embed_model}</code> or use
                                a new collection name (e.g. <code>{collection}__{embedModel.replace(/:/g,'_')}</code>).
                            </div>
                        )}
                        {profileMismatch && (
                            <div className="err" style={{ marginTop: 8 }}>
                                ⚠ Profile is <code>{profile}</code> but the collection was built with{' '}
                                <code>{collProfile}</code>. Section semantics and card recipes differ
                                across profiles, so ingestion will be refused. Either switch the
                                profile to <code>{collProfile}</code> or use a new collection name
                                (e.g. <code>{collection}__{profile}</code>).
                            </div>
                        )}
                    </div>
                );
            })()}

            <div className={`card ${(!status.running && status.done > 0) ? 'progressDone' : ''}`}>
                <h3>📈 Progress {!status.running && status.done > 0 && pct === 100 && <Tag severity="success" value="✓ Done" style={{ marginLeft: 8 }} />}</h3>
                <div className="row" style={{ alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                        {/* PrimeReact's <ProgressBar> animates a shimmer
                            even at value=100 — visually it looks like the
                            run is still ongoing. While running we keep
                            the animated bar (it's the "alive" cue); once
                            the run completes we swap to a flat coloured
                            div so the UI quiets down. */}
                        {status.running
                            ? <ProgressBar value={pct} />
                            : <StaticBar pct={pct} color={pct === 100 ? '#22863a' : '#2196f3'} height={20} />}
                    </div>
                    <span style={{ marginLeft: 12 }}>{status.done}/{status.total} files ({pct}%)</span>
                </div>
                {/* Chunk-grain progress for the file currently being embedded.
                    Without this, a single-PDF ingestion sits at 0/1 for the
                    entire run with no feedback. */}
                {status.running && status.chunk_total > 0 && (() => {
                    const cpct = Math.floor((status.chunk_done / status.chunk_total) * 100);
                    return (
                        <div className="row" style={{ alignItems: 'center', marginTop: 8 }}>
                            <div style={{ flex: 1 }}>
                                <ProgressBar value={cpct} showValue={false} style={{ height: 12 }} />
                            </div>
                            <span style={{ marginLeft: 12, fontSize: 13 }}>
                                chunk {status.chunk_done}/{status.chunk_total} of current file ({cpct}%)
                            </span>
                        </div>
                    );
                })()}
                {/* Phase 5k: synopsis pass is its own sub-progress. Each
                    matching chapter triggers one Ollama generate call,
                    which is much slower than embed_text — surfacing it
                    separately keeps users from thinking the run hung. */}
                {status.running && status.synopsis_total > 0 && (() => {
                    const spct = Math.floor((status.synopsis_done / status.synopsis_total) * 100);
                    return (
                        <div className="row" style={{ alignItems: 'center', marginTop: 6 }}>
                            <div style={{ flex: 1 }}>
                                <ProgressBar value={spct} showValue={false} style={{ height: 10 }} />
                            </div>
                            <span style={{ marginLeft: 12, fontSize: 13 }}>
                                synopsis {status.synopsis_done}/{status.synopsis_total} ({spct}%)
                            </span>
                        </div>
                    );
                })()}
                {/* Phase 5h: coref is one LLM call PER CHUNK (typically
                    thousands per novel), so its bar moves slowest of
                    all and dominates wall-clock. Distinct colour cue
                    so users can see at a glance "we're in the long
                    phase". */}
                {status.running && status.coref_total > 0 && (() => {
                    const cppct = Math.floor((status.coref_done / status.coref_total) * 100);
                    return (
                        <div className="row" style={{ alignItems: 'center', marginTop: 6 }}>
                            <div style={{ flex: 1 }}>
                                <ProgressBar value={cppct} showValue={false}
                                             color="#b65c00"
                                             style={{ height: 10 }} />
                            </div>
                            <span style={{ marginLeft: 12, fontSize: 13 }}>
                                coref {status.coref_done}/{status.coref_total} ({cppct}%) — per-chunk LLM
                            </span>
                        </div>
                    );
                })()}
                <div style={{ marginTop: 6, fontSize: 13 }}>
                    Errors: {status.errors || 0} &nbsp;|&nbsp; Last: <code>{status.last_file || '—'}</code>
                </div>
                {status.error && <div className="err">⚠ {status.error}</div>}
            </div>

            {finishSummary && (
                <div className="card finishCard">
                    <h3>✅ Ingestion finished at {finishSummary.finishedAt}</h3>
                    <div className="finishGrid">
                        <div><b>Papers processed:</b> {finishSummary.done} / {finishSummary.total}</div>
                        <div><b>Errors:</b> {finishSummary.errors || 0}</div>
                        <div><b>Duration:</b> {formatDuration(finishSummary.duration)}</div>
                    </div>
                    {finishSummary.error && <div className="err">⚠ {finishSummary.error}</div>}
                    {/* Phase 5n: chain logimport status. Surfaced from the
                        worker-side audit call. */}
                    {finishSummary.chain_logimport === 'ok' && (
                        <div style={{ marginTop: 8, fontSize: 13, color: '#22863a' }}>
                            ⛓ Chain logimport: <b>ok</b>
                            {finishSummary.chain_logimport_tx && (
                                <> · tx <code>{finishSummary.chain_logimport_tx}</code></>
                            )}
                        </div>
                    )}
                    {finishSummary.chain_logimport === 'error' && (
                        <div style={{ marginTop: 8, fontSize: 13, color: '#d73a49' }}>
                            ⛓ Chain logimport failed: {finishSummary.chain_logimport_msg || '(no detail)'}
                        </div>
                    )}
                    {finishSummary.chain_logimport === 'skipped' && (
                        <div style={{ marginTop: 8, fontSize: 13, color: '#888' }}>
                            ⛓ Chain logimport skipped (no papers ingested or run errored).
                        </div>
                    )}
                    <div className="row" style={{ marginTop: 8 }}>
                        <Button label="Dismiss" size="small" severity="secondary"
                                onClick={() => setFinishSummary(null)} />
                        <Button label="List indexed papers" size="small" icon="pi pi-list"
                                onClick={listPapers} />
                    </div>
                </div>
            )}

            <div className="card">
                <h3>📝 Recent files</h3>
                <DataTable value={recent} size="small" scrollable scrollHeight="320px" emptyMessage="No activity yet">
                    <Column field="paper_id" header="ID" style={{ width: 70 }} />
                    <Column field="filename" header="File" />
                    <Column field="title" header="Title" />
                    <Column field="status" header="Status" body={(r) => (
                        <Tag value={r.status}
                             severity={r.status === 'ok' ? 'success'
                                     : r.status === 'already_indexed' ? 'info'
                                     : r.status === 'partial' ? 'warning'
                                     : 'danger'} />
                    )} style={{ width: 110 }} />
                    <Column header="Chunks" style={{ width: 110 }}
                            body={(r) => r.failed_chunks
                                ? <span>{r.chunks} <span style={{ color: '#b00020' }}>(✗{r.failed_chunks})</span></span>
                                : (r.chunks ?? '')} />
                    <Column header="Detail" body={(r) => r.error
                        ? <span title={r.error} className="errCell">{String(r.error).slice(0, 100)}{String(r.error).length > 100 ? '…' : ''}</span>
                        : ''} />
                </DataTable>
            </div>

            <Dialog header="📁 Pick corpus directory" visible={browseOpen} style={{ width: '900px' }}
                    onHide={() => setBrowseOpen(false)} dismissableMask>
                {/* PDF count is the key signal — make it the headline so users
                    can see at a glance whether the current directory is the
                    right one to ingest. */}
                <div style={{
                    marginBottom: 12, padding: 12,
                    background: browseInfo.pdf_count > 0 ? '#e6f4ea' : '#fafafa',
                    borderRadius: 6, border: '1px solid #e1e4e8',
                    display: 'flex', alignItems: 'baseline', gap: 12,
                }}>
                    <span style={{
                        fontSize: 32, fontWeight: 700, lineHeight: 1,
                        color: browseInfo.pdf_count > 0 ? '#22863a' : '#999',
                    }}>
                        {browseInfo.pdf_count || 0}
                    </span>
                    <span style={{ fontSize: 14, color: '#444' }}>
                        PDF{browseInfo.pdf_count === 1 ? '' : 's'} found in this directory
                        {browseInfo.pdf_count === 0 && (
                            <span style={{ color: '#888', marginLeft: 6 }}>
                                — pick a subfolder below
                            </span>
                        )}
                    </span>
                </div>
                <div style={{ marginBottom: 10 }}>
                    <span style={{ fontSize: 12, color: '#666' }}>Path:</span>
                    <code style={{ marginLeft: 6, wordBreak: 'break-all' }}>{browseInfo.path || '—'}</code>
                    {browseInfo.cwd && (
                        <span style={{ marginLeft: 12, fontSize: 11, color: '#888' }}>
                            (worker cwd: <code>{browseInfo.cwd}</code>)
                        </span>
                    )}
                </div>
                <div className="row" style={{ marginBottom: 8 }}>
                    <Button icon="pi pi-arrow-up" label="Parent" size="small" disabled={!browseInfo.parent}
                            onClick={() => browseAt(browseInfo.parent)} />
                    <Button icon="pi pi-home" label="Worker cwd" size="small"
                            onClick={() => browseAt(browseInfo.cwd || '')} />
                    <Button icon="pi pi-refresh" label="Reload" size="small"
                            onClick={() => browseAt(browseInfo.path)} loading={browseLoading} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {/* Subdirectories (left column) — for navigation */}
                    <div>
                        <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                            Subdirectories ({(browseInfo.subdirs || []).length})
                            {(browseInfo.subdirs || []).length > 0 && ' — click to enter:'}
                        </div>
                        <div style={{ maxHeight: 320, overflow: 'auto', border: '1px solid #e1e4e8', borderRadius: 6 }}>
                            {(browseInfo.subdirs || []).length === 0 && (
                                <div style={{ padding: 12, color: '#888', fontSize: 13, fontStyle: 'italic' }}>
                                    None.
                                </div>
                            )}
                            {(browseInfo.subdirs || []).map((d) => (
                                <div key={d} className="browseRow"
                                     onClick={() => browseAt(browseInfo.path + (browseInfo.path.endsWith('/') ? '' : '/') + d)}>
                                    <i className="pi pi-folder" style={{ marginRight: 8, color: '#fab005' }} />{d}
                                </div>
                            ))}
                        </div>
                    </div>
                    {/* PDF list (right column) — preview of what will be ingested */}
                    <div>
                        <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                            PDFs in this directory ({browseInfo.pdf_count || 0})
                            {browseInfo.pdfs_truncated && (
                                <span style={{ color: '#d4a017' }}> — showing first 500</span>
                            )}
                        </div>
                        <div style={{ maxHeight: 320, overflow: 'auto', border: '1px solid #e1e4e8', borderRadius: 6 }}>
                            {(browseInfo.pdfs || []).length === 0 && (
                                <div style={{ padding: 12, color: '#888', fontSize: 13, fontStyle: 'italic' }}>
                                    No PDFs here. Navigate into a subdirectory or up via Parent.
                                </div>
                            )}
                            {(browseInfo.pdfs || []).map((f) => (
                                <div key={f.name} className="browseRow"
                                     style={{ cursor: 'default',
                                              display: 'flex', alignItems: 'center', gap: 6 }}
                                     title={f.name}>
                                    <i className="pi pi-file-pdf" style={{ color: '#d73a49', flexShrink: 0 }} />
                                    <span style={{ flex: 1, overflow: 'hidden',
                                                   textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                   fontSize: 13 }}>
                                        {f.name}
                                    </span>
                                    {f.size != null && (
                                        <span style={{ fontSize: 11, color: '#888', flexShrink: 0 }}>
                                            {f.size >= 1024 * 1024
                                                ? `${(f.size / 1024 / 1024).toFixed(1)} MB`
                                                : `${Math.round(f.size / 1024)} KB`}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="row" style={{ marginTop: 12, justifyContent: 'flex-end' }}>
                    <Button label="Cancel" severity="secondary" onClick={() => setBrowseOpen(false)} />
                    <Button label={browseInfo.pdf_count > 0
                                    ? `Use this directory (${browseInfo.pdf_count} PDFs)`
                                    : 'Use this directory'}
                            icon="pi pi-check"
                            disabled={!browseInfo.path || (browseInfo.pdf_count === 0)}
                            tooltip={browseInfo.pdf_count === 0
                                    ? 'No PDFs in this directory — can\'t ingest from here'
                                    : undefined}
                            tooltipOptions={{ position: 'top' }}
                            onClick={() => {
                                setCorpusDir(browseInfo.path);
                                localStorage.setItem('corpusDir', browseInfo.path);
                                setBrowseOpen(false);
                            }} />
                </div>
            </Dialog>

            {papers.length > 0 && (
                <div className="card">
                    <h3>📦 Papers in collection ({papers.length})</h3>
                    <DataTable value={papers} size="small" scrollable scrollHeight="300px" paginator rows={20}>
                        <Column field="paper_id" header="ID" sortable style={{ width: 90 }} />
                        <Column field="title" header="Title" />
                        <Column field="filename" header="File" />
                        <Column field="chunks" header="Chunks" sortable style={{ width: 90 }} />
                        <Column field="pages" header="Pages" sortable style={{ width: 80 }} />
                    </DataTable>
                </div>
            )}
        </div>
    );
};

export default DBFromCorpusPapers;
